import React, { Dispatch, SetStateAction, ChangeEvent } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { RadioGroup, Button, CheckboxOld } from 'shared/components';
import { SmallLoader } from 'shared/components/loading';
import { GqlImageBanner, GqlImageBannerPlatform, ImageBannerPlatformType, ImageBannerType } from 'types/graphql';
import { InlineBanner } from 'src/components/inline-banner';
import useHasMobileApp from 'src/utils/use-has-mobile-app';
import { PAY_BY_BANK_ALT_TXT } from 'src/dispensary-admin/customize/promotion-popup/promotion-popup';
import { validateURL, validateAlt, displayBannerPlatforms } from '../../image-banner-modal/image-banner-modal.helper';
import ImageBannerPlatform from '../../image-banner-modal/image-banner-platform';
import {
  Block,
  Title,
  Body,
  HiddenInput,
  ImportantText,
  FileSelect,
  InputContainer,
  Input,
  ValidateCheckIcon,
  PlaceHolderBanner,
  MobilePlaceHolderBanner,
  Separator,
  DesktopBannerImage,
  MobileBannerImage,
} from '../../image-banner-modal/image-banner-modal.styles';
import { ButtonContainer, TwoColumnLayout, LeftColumn, RightColumn } from './configuration-step.styles';
import {
  ConnectedBannerInfo,
  ConnectedBannerLabel,
  ConnectedBannerType,
  PlatformCheckboxContainer,
} from './connected-banner.styles';

type ConfigurationStepProps = {
  banner: GqlImageBanner | undefined;
  currentStepIndex: number;
  setStepNumber: (newStep: number) => void;
  setBannerData: (data: Partial<GqlImageBanner>) => void;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  handleFile: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  isUploadingImage: boolean;
  isUploadingMobileImage: boolean;
};

export const TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT = 'image-banner-modal-alt-input';
export const TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT_VALID = 'image-banner-modal-alt-input-valid';

const imgixParams = {
  fit: 'crop',
  crop: 'edges',
};

/**
 * Returns the display label for a connected banner type
 * Uses bannerType to differentiate between regular collections and custom categories
 * since both use connectedType: 'Collection' under the hood
 */
function getConnectedTypeLabel(connectedType: string | null | undefined, bannerType?: ImageBannerType | null): string {
  // Custom categories use connectedType: 'Collection' but bannerType: 'category'
  // This should be changed and removed when we correctly set up the connected type in the backend.
  if (connectedType === 'Collection' && bannerType === ImageBannerType.category) {
    return 'Custom Category';
  }

  switch (connectedType) {
    case 'Special':
      return 'Special';
    case 'Collection':
      return 'Collection';
    case 'Category':
      return 'Category';
    default:
      return 'Item';
  }
}

export function ConfigurationStep({
  banner,
  currentStepIndex,
  setStepNumber,
  setBannerData,
  setIsDirty,
  handleFile,
  isUploadingImage,
  isUploadingMobileImage,
}: ConfigurationStepProps): JSX.Element {
  const flags = useFlags();
  const hasMobileApp = useHasMobileApp();
  const showBannerSegmentation = flags['growth.ecomm.banner-segmentation.rollout'] ?? false;

  const bannerType = banner?.bannerType ?? ImageBannerType.link;
  const isConnectedBanner = bannerType !== ImageBannerType.link;
  const defaultLink = banner?.link ?? '';
  const defaultOpenInNewTab = banner?.openInNewTab ?? true;
  const platforms = banner?.platforms ?? [];
  const image = banner?.image ?? '';
  const mobileImage = banner?.mobileImage ?? '';
  const alt = banner?.alt ?? '';
  const isEditingBanner = !!banner?._id;
  const isPayByBankBanner = banner?.alt === PAY_BY_BANK_ALT_TXT;

  const buttonText = isEditingBanner ? 'EDIT FILE' : 'SELECT FILE';

  const linkBehaviorOptions = [
    {
      label: 'New tab',
      value: true,
    },
    {
      label: 'Current tab',
      value: false,
    },
  ];

  function handleSetLink(e: React.ChangeEvent<HTMLInputElement>): void {
    setBannerData({ ...banner, link: e.target.value });
    setIsDirty(true);
  }

  function handleChangeLinkBehavior(openInNewTab: boolean): void {
    setBannerData({ ...banner, openInNewTab });
    setIsDirty(true);
  }

  function handleSetPlatform(platform: GqlImageBannerPlatform): void {
    const existingPlatforms = [...platforms];
    const existingPlatformIndex = existingPlatforms.findIndex((p) => p?.type === platform.type);

    if (existingPlatformIndex > -1) {
      existingPlatforms[existingPlatformIndex] = platform;
    } else {
      existingPlatforms.push(platform);
    }

    setBannerData({ ...banner, platforms: existingPlatforms });
    setIsDirty(true);
  }

  /**
   * Toggle platform enabled status for connected banners (simpler than full platform config)
   */
  function handleTogglePlatformEnabled(platformType: ImageBannerPlatformType, enabled: boolean): void {
    const existingPlatforms = [...platforms];
    const existingPlatformIndex = existingPlatforms.findIndex((p) => p?.type === platformType);

    const platform: GqlImageBannerPlatform = {
      enabled,
      link: null,
      openInNewTab: true,
      type: platformType,
    };

    if (existingPlatformIndex > -1) {
      existingPlatforms[existingPlatformIndex] = platform;
    } else {
      existingPlatforms.push(platform);
    }

    setBannerData({ ...banner, platforms: existingPlatforms });
    setIsDirty(true);
  }

  function handleSetAlt(e: ChangeEvent<HTMLInputElement>): void {
    setBannerData({ ...banner, alt: e.target.value });
    setIsDirty(true);
  }

  function handleNext(): void {
    setStepNumber(currentStepIndex + 2);
  }

  const webPlatform = platforms.find((p) => p?.type === ImageBannerPlatformType.web);
  const kioskPlatform = platforms.find((p) => p?.type === ImageBannerPlatformType.kiosk);
  const iosPlatform = platforms.find((p) => p?.type === ImageBannerPlatformType.iosMobileApp);

  const requiresDesktopImage =
    !showBannerSegmentation || (webPlatform?.enabled ?? true) || (kioskPlatform?.enabled ?? true);
  const requiresMobileImage =
    !showBannerSegmentation || (webPlatform?.enabled ?? true) || ((iosPlatform?.enabled ?? true) && hasMobileApp);

  // For connected banners, we don't require a link URL
  const linkValidationPassed = isConnectedBanner || validateURL(defaultLink) || defaultLink === '';

  const platformLinksValid = platforms.every((p) => !p?.enabled || !p.link || validateURL(p.link ?? ''));

  const isNextDisabled =
    isUploadingImage ||
    isUploadingMobileImage ||
    (requiresDesktopImage && !image) ||
    (requiresMobileImage && !mobileImage) ||
    (!requiresMobileImage && !requiresDesktopImage) ||
    (!showBannerSegmentation && !linkValidationPassed) ||
    (showBannerSegmentation && !isConnectedBanner && !platformLinksValid);

  /**
   * Render platform selection for connected banner types
   * Shows only enabled/disabled toggles without URL inputs
   */
  function renderConnectedPlatforms(): JSX.Element {
    return (
      <>
        <ConnectedBannerInfo>
          <ConnectedBannerType>
            Connected to {getConnectedTypeLabel(banner?.connectedType, banner?.bannerType)}
          </ConnectedBannerType>
          <ConnectedBannerLabel>
            When clicked, customers will be directed to the{' '}
            {getConnectedTypeLabel(banner?.connectedType, banner?.bannerType).toLowerCase()} page.
          </ConnectedBannerLabel>
        </ConnectedBannerInfo>

        <Block>
          <Title>Platforms</Title>
          <Body>Select the platforms where this image banner should be displayed.</Body>
        </Block>

        {!requiresDesktopImage && !requiresMobileImage && (
          <Block>
            <InlineBanner description='At least one platform required to create image banner.' severity='error' />
          </Block>
        )}

        <PlatformCheckboxContainer>
          <CheckboxOld
            checked={webPlatform?.enabled ?? true}
            id='web-platform'
            label='Web'
            name='web-platform'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleTogglePlatformEnabled(ImageBannerPlatformType.web, e.target.checked);
            }}
            pt='0'
            width='100%'
          />
        </PlatformCheckboxContainer>

        {!isPayByBankBanner && (
          <PlatformCheckboxContainer>
            <CheckboxOld
              checked={kioskPlatform?.enabled ?? true}
              id='kiosk-platform'
              label='Kiosk'
              name='kiosk-platform'
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleTogglePlatformEnabled(ImageBannerPlatformType.kiosk, e.target.checked);
              }}
              pt='0'
              width='100%'
            />
          </PlatformCheckboxContainer>
        )}

        {hasMobileApp && (
          <PlatformCheckboxContainer>
            <CheckboxOld
              checked={iosPlatform?.enabled ?? true}
              id='ios-platform'
              label='Mobile App'
              name='ios-platform'
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleTogglePlatformEnabled(ImageBannerPlatformType.iosMobileApp, e.target.checked);
              }}
              pt='0'
              width='100%'
            />
          </PlatformCheckboxContainer>
        )}
      </>
    );
  }

  /**
   * Renders the left column content based on banner type and feature flags
   */
  function renderLeftColumnContent(): JSX.Element {
    // Connected banners show simplified platform selection
    if (isConnectedBanner) {
      return renderConnectedPlatforms();
    }

    // Legacy link configuration (without banner segmentation)
    if (!showBannerSegmentation) {
      return (
        <>
          <Block>
            <Title>Link</Title>
            <Body>Add a link to your image. When your image is clicked, a customer will be directed to this link.</Body>
            <InputContainer>
              <Input type='url' value={defaultLink} onChange={handleSetLink} />
              <ValidateCheckIcon valid={validateURL(defaultLink) ? 1 : 0} />
            </InputContainer>
          </Block>

          <Block>
            <Title>Link Behavior</Title>
            <Body>Decide where your image banner link opens when a customer clicks it.</Body>
            <RadioGroup
              options={linkBehaviorOptions}
              display={['inline-block']}
              defaultValue={defaultOpenInNewTab}
              onChange={handleChangeLinkBehavior}
            />
          </Block>
        </>
      );
    }

    // Platform-specific configuration (with banner segmentation)
    return (
      <>
        <Block>
          <Title>Platforms</Title>
          <Body>
            Select the platforms where the image banner should be displayed, and include a link if applicable. When the
            banner image is clicked, the customer wil be directed to the link provided per platform.
          </Body>
        </Block>

        {!requiresDesktopImage && !requiresMobileImage && (
          <Block>
            <InlineBanner description='At least one platform required to create image banner.' severity='error' />
          </Block>
        )}

        {Object.values(displayBannerPlatforms).map((item) => {
          const isWebPlatform = item.value === ImageBannerPlatformType.web;
          const platform: GqlImageBannerPlatform = platforms.find((p) => p?.type === item.value) ?? {
            enabled: true,
            link: isWebPlatform ? defaultLink : null,
            openInNewTab: isWebPlatform ? defaultOpenInNewTab : true,
            type: item.value,
          };

          if (platform.type === ImageBannerPlatformType.iosMobileApp && !hasMobileApp) {
            return null;
          }

          // Hide the kiosk platform checkbox/option for Pay by Bank banners
          if (isPayByBankBanner && platform.type === ImageBannerPlatformType.kiosk) {
            return null;
          }

          return (
            <ImageBannerPlatform
              key={item.value}
              description={item.description}
              label={item.label}
              linkBehaviorDescription={item.linkBehaviorDescription}
              platform={platform}
              setPlatform={handleSetPlatform}
            />
          );
        })}
      </>
    );
  }

  return (
    <>
      <TwoColumnLayout>
        <LeftColumn>{renderLeftColumnContent()}</LeftColumn>

        <RightColumn>
          {requiresDesktopImage && (
            <>
              {image ? (
                <DesktopBannerImage
                  imgixParams={{ ...imgixParams, ar: '6:2', fill: 'blur' }}
                  src={image}
                  htmlAttributes={{ alt }}
                />
              ) : (
                <PlaceHolderBanner>Desktop image preview will show here.</PlaceHolderBanner>
              )}

              <Block>
                <Title>Desktop Image</Title>
                <Body>Uploaded images should be 2400px (width) 800px (height), and in JPEG, PNG, or GIF formats.</Body>
                <HiddenInput
                  id='image-banner-file-input'
                  type='file'
                  name='image-banner'
                  accept='image/png, image/jpeg, image/gif, image/webp, image/avif'
                  onChange={(e) => {
                    void handleFile(e);
                    e.target.value = '';
                  }}
                  disabled={isUploadingImage || isUploadingMobileImage}
                />
                <FileSelect htmlFor='image-banner-file-input'>
                  {isUploadingImage ? <SmallLoader height={20} color='#0b99e6' /> : buttonText}
                </FileSelect>
              </Block>
            </>
          )}

          {requiresMobileImage && (
            <>
              {mobileImage ? (
                <MobileBannerImage
                  imgixParams={{ ...imgixParams, ar: '2.22:1', fill: 'blur' }}
                  src={mobileImage}
                  htmlAttributes={{ alt }}
                />
              ) : (
                <MobilePlaceHolderBanner>Mobile image preview will show here.</MobilePlaceHolderBanner>
              )}

              <Block>
                <Title>Mobile image</Title>
                <Body>
                  Uploaded images should be 800px (width) 360px (height), and in JPEG, PNG, or GIF formats.
                  {(iosPlatform?.enabled ?? true) && hasMobileApp && (
                    <div>
                      <br />
                      <ImportantText>Important: </ImportantText>
                      If using this image for your mobile app, to comply with App Store guidelines, do not upload images
                      that include vape products such as disposables, pods, cartridges, and vaporizers themselves. This
                      may result in your app being rejected.
                    </div>
                  )}
                </Body>
                <HiddenInput
                  id='mobile-image-banner-file-input'
                  type='file'
                  name='mobile-image-banner'
                  accept='image/png, image/jpeg, image/gif, image/webp, image/avif'
                  onChange={(e) => {
                    void handleFile(e);
                    e.target.value = '';
                  }}
                  disabled={isUploadingImage || isUploadingMobileImage}
                />
                <FileSelect htmlFor='mobile-image-banner-file-input'>
                  {isUploadingMobileImage ? <SmallLoader height={20} color='#0b99e6' /> : buttonText}
                </FileSelect>
              </Block>
            </>
          )}

          {(requiresDesktopImage || requiresMobileImage) && (
            <>
              <Separator />

              <Block>
                <Title>Image Alt Text</Title>
                <Body>Describe your image to improve accessibility and SEO.</Body>
                <InputContainer>
                  <Input
                    type='text'
                    value={alt}
                    onChange={handleSetAlt}
                    data-testid={TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT}
                  />
                  <ValidateCheckIcon
                    valid={validateAlt(alt) ? 1 : 0}
                    data-testid={TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT_VALID}
                  />
                </InputContainer>
              </Block>
            </>
          )}
        </RightColumn>
      </TwoColumnLayout>

      <ButtonContainer>
        <Button onClick={handleNext} disabled={isNextDisabled}>
          Next
        </Button>
      </ButtonContainer>
    </>
  );
}
